import { useState } from "react";
import { ChevronsUpDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { SelectableRow } from "@/components/ui/SelectableRow";

export interface MultiSelectOption {
  id: string;
  label: string;
}

interface MultiSelectComboboxProps {
  options: MultiSelectOption[];
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
}

export function MultiSelectCombobox({
  options,
  selectedIds,
  onSelectionChange,
  placeholder = "select_options",
  searchPlaceholder = "search_options",
  emptyMessage = "no_options_found",
  disabled = false,
  className,
}: MultiSelectComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const toggleOption = (optionId: string) => {
    const newValue = selectedIds.includes(optionId)
      ? selectedIds.filter((id) => id !== optionId)
      : [...selectedIds, optionId];
    onSelectionChange(newValue);
  };

  const removeOption = (optionId: string) => {
    const newValue = selectedIds.filter((id) => id !== optionId);
    onSelectionChange(newValue);
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(options.map((opt) => opt.id));
    }
  };

  const selectedOptions = options.filter((opt) =>
    selectedIds.includes(opt.id)
  );

  const isAllSelected = selectedIds.length === options.length && options.length > 0;

  return (
    <div className={cn("w-full space-y-2", className)}>
      {/* Combobox trigger */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between font-normal h-9",
              selectedOptions.length === 0 && "text-muted-foreground",
              disabled && "disabled:opacity-50 disabled:cursor-not-allowed !bg-transparent"
            )}
            disabled={disabled}
          >
            <span className="truncate">
              {selectedOptions.length === 0
                ? placeholder
                : selectedOptions.length === 1
                ? selectedOptions[0].label
                : t("multiSelect.items_selected", { count: selectedOptions.length })}
            </span>
            {!disabled && (
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
          <Command>
            <CommandInput
              placeholder={searchPlaceholder}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup className="p-1">
                {/* Select All option */}
                {options.length > 1 && (                  
                  <SelectableRow
                    label={t("multiSelect.select_all")}
                    isSelected={isAllSelected}
                    onToggle={handleSelectAll}
                    disabled={disabled}
                    className="font-medium"
                  />                
                )}
                
                {options.map((option) => {
                  const isSelected = selectedIds.includes(option.id);
                  return (
                    <CommandItem
                      key={option.id}
                      value={option.label}
                      onSelect={() => {
                        if (!disabled) {
                          toggleOption(option.id);
                        }
                      }}
                      className="p-0 aria-selected:bg-transparent"
                      disabled={disabled}
                    >
                      <div className="w-full pointer-events-none">
                        <SelectableRow
                          label={option.label}
                          isSelected={isSelected}
                          onToggle={() => {}}
                          disabled={disabled}
                        />
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected badges - displayed below the trigger (only when multiple selections) */}
      {selectedOptions.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedOptions.map((option) => (
            <Badge
              key={option.id}
              variant="secondary"
              className="pl-2 pr-1 py-1 text-xs font-normal"
            >
              {option.label}
              {!disabled && (
                <button
                  type="button"
                  className="ml-1 rounded-full outline-none hover:bg-muted inline-flex items-center justify-center"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      removeOption(option.id);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeOption(option.id);
                  }}
                  disabled={disabled}
                >
                  <X className="h-3 w-3 text-inherit" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

